import React, { useState, FormEvent, useEffect } from 'react';
import { Suggest, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, Button, Checkbox, HTMLTable, MenuItem, Spinner } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import { CustomerActivityList, CustomerActivity, CustomerActivityInvoice, CustomerActivityInvoiceItem, Environment, Customer } from './Types'
import * as utils from "./utils";

interface ReceivableAgingReportProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function ReceivableAgingReport({ svc, currentEnv = {} as Environment }: ReceivableAgingReportProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState({} as CustomerActivityList)
    const [totals, setTotals] = useState({} as Map<string, number[]>)
    const [grandTotals, setGrandTotals] = useState([0,0,0,0,0,0])
    const [custList, setCustList] = useState([] as Customer[])

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    const [fromCust, setFromCust] = useState({} as Customer)
    const [toCust, setToCust] = useState({} as Customer)
    //const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    const [from, setFrom] = useState(utils.formatDate(new Date(currentEnv.startOfYear)))
    const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)
    const [paid, setPaid] = useState(false)

    const refreshCustList = async () => {
        if ("envId" in currentEnv) {
            const res = await svc.post('/customerlist');
            //console.log("refreshCustList", res.data.data)
            setCustList([{custCode: "Any", custName: "Any"} as Customer ].concat(res.data.data))
        }
    }

    useEffect(() => {
        refreshCustList();
    }, [svc, currentEnv])
    
    const custRenderer: ItemRenderer<Customer> = (cust, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        const text = `${cust.custName}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={cust.custCode}
                key={cust.custCode}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const custPredicate: ItemPredicate<Customer> = (query, cust, _index, exactMatch) => {
        const normalizedText = `${cust.custCode} - ${cust.custName}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const custValueRenderer = (cust: Customer) => {
        return cust.custCode
    }

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/customeractivity',
                {
                    fromCustCode: fromCust.custCode === "Any" ? "" : fromCust.custCode,
                    toCustCode: toCust.custCode === "Any" ? "" : toCust.custCode,
                    fromDate: from,
                    toDate: to,
                    includesPaid: paid,
                });

            setIsLoading(false)
            //console.log(res.data.data.data.length)
            let newTotals = new Map<string, number[]>()
            let newGrandTotals = [0, 0, 0, 0, 0, 0]
            for (let i = 0; i < res.data.data.data.length; i++) {
                let cust = res.data.data.data[i] as CustomerActivity
                let custTotals = [0, 0, 0, 0, 0, 0]
                for (let j = 0; j < cust.invoices.length; j++) {
                    let inv = cust.invoices[j] as CustomerActivityInvoice
                    custTotals[0] += inv.outstanding
                    custTotals[1] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), -99999, 0) ? inv.outstanding : 0
                    custTotals[2] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 1, 30) ? inv.outstanding : 0
                    custTotals[3] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 31, 60) ? inv.outstanding : 0
                    custTotals[4] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 61, 90) ? inv.outstanding : 0
                    custTotals[5] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 91, 99999) ? inv.outstanding : 0
                    /*
                    for (let k = 0; k < inv.items.length; k++) {
                        let item = inv.items[k]
                    }
                    */
                }
                newGrandTotals[0] += custTotals[0]
                newGrandTotals[1] += custTotals[1]
                newGrandTotals[2] += custTotals[2]
                newGrandTotals[3] += custTotals[3]
                newGrandTotals[4] += custTotals[4]
                newGrandTotals[5] += custTotals[5]
                newTotals.set(cust.custCode, custTotals)
                //console.log(cust.custCode, custTotals)
            }
            setTotals(newTotals)
            setGrandTotals(newGrandTotals)
            setData(res.data.data)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = "1310px"
    let pageTitle = t('receivable_aging_report')
    let list = <div className="Print-Align-C Print-90-Percent" style={{maxWidth: pageMaxWidth}}>
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-8" />
        {/*
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "14px", fontWeight: 600}}>Account From: {data.fromAcCode === "" ? "Any" : data.fromAcCode}<div className="Spacing-H-8"/>To: {data.toAcCode === "" ? "Any" : data.toAcCode}</div>
        </div>
        */}
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {data.fromDate}<div className="Spacing-H-8"/>{t('to')}: {data.toDate}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-InvoiceNo">{t('invoice_no')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-Date">{t('due_date')}</th>
                    <th className="TCol-TxType">{t('type')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('amount')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('undue')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('<= 30 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('31 - 60 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('61 - 90 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('> 90 days')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((cust: CustomerActivity) => <>
                    <tr>
                        <td className="TCol-InvoiceNo"><b>{cust.custCode}</b></td>
                        <td className="TCol-Date" colSpan={3}><b>{cust.custName}</b></td>
                        {/*
                        <td className="TCol-Date"></td>
                        <td className="TCol-TxType"></td>
                        */}
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                    </tr>
                    {cust.invoices?.map((inv: CustomerActivityInvoice) => <>
                    {/* <tr>
                        <td className="TCol-InvoiceNo">{inv.invNo}</td>
                        <td className="TCol-Date">{inv.invDate}</td>
                        <td className="TCol-Date">{inv.dueDate}</td>
                        <td className="TCol-TxType">{inv.txType}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(inv.amount)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), -99999, 0) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 1, 30) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 31, 60) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 61, 90) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 91, 99999) && utils.formatMoney(inv.outstanding)}</div></td>
                    </tr> */}
                    {inv.items.map((item: CustomerActivityInvoiceItem, i: number) =>
                    <tr>
                        <td className="TCol-InvoiceNo">{i === 0 && inv.invNo}</td>
                        <td className="TCol-Date">{item.date}</td>
                        <td className="TCol-Date">{item.dueDate}</td>
                        <td className="TCol-TxType">{item.txType}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(item.amount)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), -99999, 0) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 1, 30) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 31, 60) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 61, 90) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 91, 99999) && utils.formatMoney(inv.outstanding)}</div></td>
                    </tr>
                    )}
                    </>)}
                    <tr>
                        <td className="TCol-InvoiceNo"></td>
                        <td className="TCol-Date" colSpan={2}><b>{t('TOTALS')}</b></td>
                        {/*<td className="TCol-Date"></td>*/}
                        <td className="TCol-TxType"></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![0])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![1])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![2])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![3])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![4])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(cust.custCode) && utils.formatMoney(totals.get(cust.custCode)![5])}</b></div></td>
                    </tr>
                    </>
                )}
                <tr>
                    <td className="TCol-InvoiceNo"></td>
                    <td className="TCol-Date" colSpan={2}><b>{t('GRAND_TOTALS')}</b></td>
                    {/*<td className="TCol-Date"></td>*/}
                    <td className="TCol-TxType"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[0])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[1])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[2])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[3])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[4])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(grandTotals[5])}</b></div></td>
                </tr>
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="fromAcCode">
                        {/*<InputGroup id="fromAcCode" value={fromAcCode} onChange={utils.handleStringChange(s => setFromAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('customer'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                selectedItem={fromCust}
                                items={custList}
                                itemRenderer={custRenderer}
                                itemPredicate={custPredicate}
                                inputValueRenderer={custValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(cust) => {
                                    setFromCust(cust)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('to')} labelFor="toAcCode">
                        {/*<InputGroup id="toAcCode" value={toAcCode} onChange={utils.handleStringChange(s => setToAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('customer'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                selectedItem={toCust}
                                items={custList}
                                itemRenderer={custRenderer}
                                itemPredicate={custPredicate}
                                inputValueRenderer={custValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(cust) => {
                                    setToCust(cust)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-3-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    <FormGroup className="Col-4-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-5-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                </div>
                <div className="Row">
                    {/* Checkbox-LabeledFormGroup */}
                    <Checkbox className="Col-1-1" label={t('includes_paid')} checked={paid} onChange={utils.handleBooleanChange(v => setPaid(v))} />
                </div>
            </form>
            {list}
        </>
    );
}

export default ReceivableAgingReport